import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { M3 } from '@messenger/consts'
import { produce } from 'immer'
import { default as _set } from 'lodash/set'
import { default as _omit } from 'lodash/omit'
import { useSetToast } from 'hooks'
import { editorPagesAtom, unsavedChangesAtom } from '@/atoms/_editorAtoms'
import {
  isPageDeleting,
  selectedPageAtom,
  triggerUseEffect,
} from '@/atoms/editorAtoms'
import { applicationTypeAtom } from '@/atoms/globalAtoms'
import {
  useRecoilState,
  useRecoilCallback,
  useSetRecoilState,
  useRecoilValue,
} from 'recoil'
import { arrayMove, arrayRemove } from 'react-movable'
import { v4 as uuidv4 } from 'uuid'
import { getImageDimensions } from 'utils'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { editorSelfDeletedPageIdsAtom } from '@/atoms/editorTextBoxAtoms'
import { useDropEditorTextBoxWork } from '@/hooks/editor/useDropEditorTextBoxWork'
import isEven from 'utils/global/isEven'
import zLayers from 'utils/styling/zLayers'
import regEx from 'utils/global/regEx'
import { useTranslation } from 'react-i18next'
import { archiveOrderPage, createOrderPage } from '@/api/orderPages'
import { isGuestbookCoverPage } from 'utils/editor/isGuestbookCoverPage'

// `disabled` is a UI-only flag react-movable reads to lock the guestbook cover.
// It must never reach editorPagesAtom, which Editor.save serializes into
// json_template/content — strip it before persisting the reordered page set.
const stripUiFields = (pages) => pages.map((page) => _omit(page, ['disabled']))

export const usePageShuffler = (basePages) => {
  const handleError = useErrorHandler('usePageShuffler')
  const dropTextBoxWorkForPages = useDropEditorTextBoxWork()
  const setSelfDeletedPageIds = useSetRecoilState(editorSelfDeletedPageIdsAtom)
  const [pages, setPages] = useState([])
  const [disabled, setDisableAlterPages] = useState(null)
  const [loading, setLoading] = useState(true)
  const [blankPage, setBlankPage] = useState({})
  const [preventDelete, setPreventDelete] = useState(false)
  const [selectedPage, setSelectedPage] = useRecoilState(selectedPageAtom)
  const [editorPages, setEditorPages] = useRecoilState(editorPagesAtom)
  const [isDeleting, setIsDeleting] = useRecoilState(isPageDeleting)
  const setUnsavedChanges = useSetRecoilState(unsavedChangesAtom)
  const setTrigger = useSetRecoilState(triggerUseEffect)
  const { search } = useLocation()
  const applicationTypeValue = useRecoilValue(applicationTypeAtom)
  //createOrderPage routes MDL vs M3 by application type on the backend; without
  //it an M3 add hits the MDL order_pages table and fails the FK on the M3 set
  //id. Derive the type synchronously from the m3OrderProductId query param
  //(mirrors Editor.currentApplicationType): applicationTypeAtom only flips to M3
  //via an effect, so reading the atom directly can still be 'MDL' on first
  //render of an M3 route and route an early Add Page to the wrong table.
  const applicationType = useMemo(() => {
    const hasM3OrderProductId = new URLSearchParams(search).has(
      'm3OrderProductId',
    )
    return hasM3OrderProductId ? M3 : applicationTypeValue
  }, [search, applicationTypeValue])

  const setToast = useSetToast()
  const { t } = useTranslation()

  const getPages = useRecoilCallback(({ snapshot }) => async () => {
    //use the callback so we can edit the page object
    let allPages = await snapshot.getPromise(editorPagesAtom)
    //don't continue unless we actually got pages back
    if (allPages.length > 0) {
      //set the disabled sets of pages, not all pages can add/drag/remove
      // logic always set as false due to dignity direct line
      const isDisabled = false
      setDisableAlterPages(isDisabled)
      //derive the 'add page' template from the first INTERIOR page, not the
      //guestbook cover: the cover is a different physical size (split into its
      //own PDF), so a cover-shaped new page is rejected by the backend and never
      //belongs in the interior set. Falls back to page 1 for non-guestbook sets.
      const templateSource =
        allPages.find((p) => !isGuestbookCoverPage(p, allPages)) ?? allPages[0]
      const page = { ...templateSource }
      //from that page create a blank page for the 'add page' we will need to make further changes to this later
      const blank = produce(page, (draft) => {
        _set(draft, 'product.items', [])
      })
      //mark each item for the drag package: react-movable refuses to drag items
      //with disabled=true. Only the guestbook cover page (a different physical
      //size, split into its own PDF) stays locked in position 1 — every other
      //page is freely reorderable by all users.
      allPages = allPages.map((page) => {
        const newPage = produce(page, (draft) => {
          _set(draft, 'disabled', isGuestbookCoverPage(page, allPages))
        })
        return newPage
      })
      //set the pages array, loading status, and blank page
      setPages(allPages)
      setLoading(false)
      setBlankPage(blank)
    }
  })

  useEffect(() => {
    getPages()
    // eslint-disable-next-line
  }, [editorPages])

  const createImageObj = async (src) => {
    const srcPrefix = process.env.REACT_APP_S3BUCKET_FLORAL_PIX
    const uuid = uuidv4()
    const d = await getImageDimensions(`${srcPrefix || ''}${src}`)
    const height = 150
    const width = height * (d.width / d.height)
    return {
      id: uuid,
      type: 'image',
      x: 0,
      y: 0,
      z: zLayers.content,
      height: height,
      width: width,
      properties: {
        subtype: 'floralPix',
      },
      image: {
        url: `${srcPrefix || ''}${src}`,
        edits: {},
      },
    }
  }

  const createTextObj = (textValue) => {
    const uuid = uuidv4()
    return {
      id: uuid,
      type: 'text',
      // is the register book the same width? Will the vertical line ever change?
      x: 77.5,
      y: 24,
      z: zLayers.content,
      height: 48,
      width: 325,
      properties: {},
      text: {
        value: `<p data-case-detail=""><span style="font-size: 36pt;">${textValue}</span></p>`,
      },
    }
  }

  const imagePlacement = (centerLine, index) => {
    const topOffset = 112
    const imageSize = 150
    const gutter = 32
    const imageNumber = index + 1
    const isOnTheRight = isEven(imageNumber)

    // handle column offset
    const x = isOnTheRight
      ? centerLine - imageSize * 0.5 + gutter
      : centerLine - imageSize * 1.5 - gutter

    // handle row offset
    let y

    if (imageNumber.toString().match(regEx.oneOrTwo)) {
      y = topOffset
    }

    if (imageNumber.toString().match(regEx.threeOrFour)) {
      y = topOffset + gutter + imageSize
    }

    if (imageNumber.toString().match(regEx.fiveOrSix)) {
      y = topOffset + gutter * 2 + imageSize * 2
    }

    return [x, y]
  }

  const addPage = useCallback(
    async (arrangements) => {
      //guard: the page shuffler + Add Page activate for messenger-user roles
      //(useIsAdmin treats messenger-user as admin). Only proceed with a valid
      //base shell and a populated interior-derived blank — otherwise we'd POST a
      //malformed page the backend rejects, leaving an undefined slot in state.
      if (!basePages?.[0] || !blankPage?.product) {
        setToast({ text: t('toast.newPageError') })
        return
      }
      //to add a page, we need to send a new page to the db, create a page payload using the blank page, omitting info the db won't take
      const blankPageDetail = _omit(basePages[0], [
        'archived',
        'id',
        'json_template',
      ])
      //give the blank page a new page number (it will be the last page in the set)
      const newBlank = produce({ ...blankPage }, (draft) => {
        _set(draft, 'pageProperties.pageNumber', pages.length + 1)
      })
      //stringify the json to send to the db
      blankPageDetail.json_template = JSON.stringify(newBlank)

      const createRespPage = async (blankPageDetail) => {
        try {
          const { status, data } = await createOrderPage(
            blankPageDetail,
            applicationType,
          )
          if (status === 201 && data) {
            data.json_template.product.orderPageId = data.id
            return data.json_template
          }
        } catch (err) {
          handleError(err?.response?.data?.message || err.message, err)
        }
      }

      const createPageObjectWithItems = async (items) => {
        const respPage = await createRespPage(blankPageDetail)
        //createRespPage returns undefined on a failed/non-201 create; bail so we
        //never spread undefined into a page object the shuffler can't render
        if (!respPage) return null
        items = items.map((item, i) => {
          const [x, y] = imagePlacement(
            basePages[0].json_template.product.alignmentPoints.vertical[0],
            i,
          )
          return {
            ...item,
            x,
            y,
          }
        })
        items.push(createTextObj('Floral Tributes'))
        return {
          ...respPage,
          product: {
            ...respPage.product,
            items,
          },
        }
      }

      //add the new page from the db to the pages array and then update all the page numbers just in case anything changed.
      const updatePageNumbers = (newPages) =>
        newPages.map((page, i) => {
          const newField = produce(page, (draft) => {
            _set(draft, 'pageProperties.pageNumber', i + 1)
          })
          return newField
        })

      // if arrangements is passed, create array of response pages with relevant image items
      if (arrangements && arrangements.length > 0) {
        const respPages = []
        for (let i = 0; i < arrangements.length; i++) {
          const imageItems = await Promise.allSettled(
            arrangements[i].images.map((src) => createImageObj(src)),
          )

          const sanitizedImageItems = imageItems.reduce(
            (result, promiseObj) => {
              if (promiseObj.status === 'fulfilled') {
                result.push(promiseObj.value)
              }

              return result
            },
            [],
          )

          // handle overflow of images on the page. Only 6 are allowed per page
          if (sanitizedImageItems.length > 6) {
            const copy = [...sanitizedImageItems]
            const remainder = copy.length % 6
            const leftOver = copy.splice(5, remainder)

            const overflowPages = [
              await createPageObjectWithItems(copy),
              await createPageObjectWithItems(leftOver),
            ]
            //drop any page whose create failed so no undefined slot reaches state
            respPages.push(...overflowPages.filter(Boolean))
          } else {
            const created = await createPageObjectWithItems(sanitizedImageItems)
            if (created) respPages.push(created)
          }
          const newPages = updatePageNumbers([...pages, ...respPages])
          setPages(newPages)
          setEditorPages(stripUiFields(newPages))
        }
        if (respPages.length === 0) {
          setToast({ text: t('toast.newPageError') })
          return
        }
        setToast({
          text: t('toast.newPagesAdded', { count: respPages.length }),
        })
      } else {
        const respPage = await createRespPage(blankPageDetail)
        //never push an undefined page into state — surface the failure instead
        if (!respPage) {
          setToast({ text: t('toast.newPageError') })
          return
        }
        setToast({ text: t('toast.newPageAdded') })
        const newPages = updatePageNumbers([...pages, respPage])
        setPages(newPages)
        setEditorPages(stripUiFields(newPages))
      }
      setTrigger(Date.now())
    },
    // eslint-disable-next-line
    [basePages, blankPage, pages, applicationType],
  )

  useEffect(() => {
    if (pages.length <= 1) {
      setPreventDelete(true)
    } else {
      setPreventDelete(false)
    }
  }, [pages])

  const updatePages = useCallback((pages, oldIndex, newIndex) => {
    //the guestbook cover is locked to position 1: react-movable only checks
    //`disabled` on the dragged item, never on the drop target, so guard here to
    //stop another page being dropped into slot 0 (or the cover being moved out)
    if (
      isGuestbookCoverPage(pages[0], pages) &&
      (oldIndex === 0 || newIndex === 0)
    ) {
      return
    }
    //use the built in arrayMove function to reorder the pages array
    let newPages = arrayMove(pages, oldIndex, newIndex)

    //update all the page numbers with the new positions
    newPages = newPages.map((page, index) => {
      const newField = produce(page, (draft) => {
        _set(draft, 'pageProperties.pageNumber', index + 1)
      })
      return newField
    })
    //set the pages and editorPages (trigger save)
    setPages(newPages)
    setEditorPages(stripUiFields(newPages))
    //a drop where the page was leaves nothing to save
    if (oldIndex !== newIndex) setUnsavedChanges(true)
    // eslint-disable-next-line
  }, [])

  const deletePage = useCallback(
    (e, index) => {
      try {
        setIsDeleting(true)
        e.stopPropagation()
        if (selectedPage === index + 1) {
          if (index !== 0) {
            setSelectedPage(index)
          } else {
            setSelectedPage(1)
          }
        }

        //get the orderPageId from the page[i].product
        const {
          product: { orderPageId },
        } = pages[index]
        //this page's row is about to be deleted, so its unsaved text-box
        //edits go with it. Left behind, the next Save would send them for a
        //page the line no longer has.
        dropTextBoxWorkForPages([orderPageId])
        //and a save already in flight for this page will come back with the
        //same page-is-gone answer a layout change made elsewhere produces.
        //Recorded before the delete goes out, this id is what tells the save
        //path that the page went away because the user asked it to.
        setSelfDeletedPageIds((prev) => ({ ...prev, [orderPageId]: true }))
        //send the archive request off to the db; the tab has already let the
        //page go. A delete the server refused leaves the page on the line, so
        //its id must not keep reading as "deleted here": a later change made
        //elsewhere would then pass for the user's own. The archive is stored
        //at once; what the delete leaves for a Save is the tab's page list
        //without the page, renumbered, so any delete the server took marks
        //the editor unsaved, and a refused one marks nothing.
        archiveOrderPage(orderPageId).then(
          () => setUnsavedChanges(true),
          (err) => {
            setSelfDeletedPageIds((prev) => {
              const { [orderPageId]: _refused, ...rest } = prev
              void _refused
              return rest
            })
            handleError(err?.response?.data?.message || err.message, err)
          },
        )
        //use the built in arrayRemove to remove the page from the pages array
        let newPages = arrayRemove(pages, index)
        //update all the pageNumbers
        newPages = newPages.map((page, i) => {
          const newField = produce(page, (draft) => {
            _set(draft, 'pageProperties.pageNumber', i + 1)
          })
          return newField
        })
        //set the pages and editorPages (trigger save)
        //TODO get rid of this timeout!
        setTimeout(() => {
          setIsDeleting(false)
          setPages(newPages)
          setEditorPages(stripUiFields(newPages))
        }, 250)
      } catch (error) {
        handleError(error?.response?.data?.message || error.message, error)
      }
    },
    // eslint-disable-next-line
    [pages, selectedPage],
  )

  const goToPage = useCallback((index) => {
    //this will just set the selectedPage (page being viewed in the editor)
    setSelectedPage(index + 1)
    // eslint-disable-next-line
  }, [])

  return {
    loading,
    disabled,
    preventDelete,
    pages,
    addPage,
    updatePages,
    deletePage,
    goToPage,
    isDeleting,
  }
}

export default usePageShuffler
